import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { ClickAwayListener, Popper } from '@mui/material';
import logo from 'assets/icons/logo.svg';
import motorolaIcon from 'assets/icons/motorolaIcon.svg';
import stores from 'assets/icons/stores.svg';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStoreSelector } from 'v3/data/store';
import { getStorePermissions } from 'v3/data/store/stores/actions';
import { StoreProfileEnum } from 'v3/data/store/stores/enums';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { usePreferenceOptionActive } from 'v3/hooks/useIsPreferenceOptionActive';
import { RoutesEnum } from 'v3/routes/enum';
import * as api from 'v3/services/api';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { PreferencesOptionsEnum } from 'v3/shared/enums/preferences-options-enum';
import { SubtitleMedium, SubtitleMediumBold } from 'v3/shared/typography/SubtitleMedium';
import { MenuSelect } from '../Menu';
import {
  DrawerContainer,
  DrawerStyled,
  DropdownPaper,
  ImgStyled,
  NavigationButtons,
  NavigationGroup,
  paperStyle,
} from './styles';

export const NavBar = () => {
  const { isDesktop, isMobile } = useBreakpoints();
  const navigate = useNavigate();
  const location = useLocation();

  const { openProductList, isMaster, isGroupAdmin, isAdmin, isStore } = getStorePermissions();
  const [sellerFeatureEnabled, setSellerFeatureEnabled] = useState<boolean>(false);
  const [paymentFeatureEnabled, setPaymentFeatureEnabled] = useState<boolean>(false);

  const verifyPath = (pathName: string): boolean => pathName === location.pathname;

  const financePaths = [RoutesEnum.ContractingList, RoutesEnum.ContractingOnline];
  const isFinanceActive = financePaths.some((path) => verifyPath(path));

  const storesPaths = [RoutesEnum.StoresList, RoutesEnum.GroupsList, RoutesEnum.SellerList, RoutesEnum.ProductsList];
  const isStoresActive = storesPaths.some((path) => verifyPath(path));

  const { storeLogged } = useStoreSelector((store) => store.store);

  const canViewOnlineContracts =
    isMaster ||
    storeLogged.group === StoreProfileEnum.IN_APP ||
    storeLogged.group === StoreProfileEnum.ONLINE ||
    storeLogged.group === StoreProfileEnum.LOM;

  const handleButtonClick = (route: string) => {
    navigate(route);
  };

  const fetchFeatureFlags = useCallback(async () => {
    try {
      const sellerFeatureFlag = await api.getFlagStatus(FeatureFlagEnum.IKB2C_4429_ENABLE_SELLER_FEATURE);
      setSellerFeatureEnabled(sellerFeatureFlag.data.enabled);

      const paymentFeatureFlag = await api.getFlagStatus(FeatureFlagEnum.IKCLOUD_33367_ENABLE_PIX_PAYMENT);
      setPaymentFeatureEnabled(paymentFeatureFlag.data.enabled);
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    }
  }, []);

  useEffect(() => {
    fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  const [openMenu, setOpenMenu] = useState<null | 'finance' | 'stores'>(null);

  const financeRef = useRef<HTMLDivElement>(null);
  const storesRef = useRef<HTMLDivElement>(null);

  const handleToggle = (menu: 'finance' | 'stores') => {
    setOpenMenu((prev) => (prev === menu ? null : menu));
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };

  const groupAdminCatalogFeatureEnabled = usePreferenceOptionActive(PreferencesOptionsEnum.GROUP_CATALOG);
  const storeOptions: { label: string; route: RoutesEnum }[] = (() => {
    if (isMaster) {
      return [
        { label: 'Lojas', route: RoutesEnum.StoresList },
        { label: 'Grupos', route: RoutesEnum.GroupsList },
        ...(sellerFeatureEnabled ? [{ label: 'Consultores', route: RoutesEnum.SellerList }] : []),
        ...[{ label: 'Ranking de consultores', route: RoutesEnum.SellersRankingList }],
        ...(openProductList ? [{ label: 'Produtos', route: RoutesEnum.ProductsList }] : []),
      ];
    }

    if (isGroupAdmin) {
      return [
        ...(sellerFeatureEnabled ? [{ label: 'Consultores', route: RoutesEnum.SellerList }] : []),
        ...(openProductList || groupAdminCatalogFeatureEnabled
          ? [{ label: 'Produtos', route: RoutesEnum.ProductsList }]
          : []),
      ];
    }

    return openProductList && !groupAdminCatalogFeatureEnabled
      ? [{ label: 'Produtos', route: RoutesEnum.ProductsList }]
      : [];
  })();

  return (
    <DrawerStyled PaperProps={paperStyle} variant="permanent" anchor="top" open>
      <DrawerContainer>
        {isDesktop ? <ImgStyled alt="logo" src={logo} /> : <img src={motorolaIcon} alt="Logo da motorola" />}

        <NavigationGroup>
          <div ref={financeRef} style={{ position: 'relative' }}>
            <NavigationButtons onClick={() => handleToggle('finance')} $active={isFinanceActive}>
              <ListAltRoundedIcon />
              {isDesktop && (
                <>
                  <SubtitleMediumBold style={{ flex: 1 }}>Financiamento</SubtitleMediumBold>
                  {openMenu === 'finance' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </>
              )}
            </NavigationButtons>
          </div>
          <Popper
            open={openMenu === 'finance'}
            anchorEl={financeRef.current}
            placement={!isMobile ? 'bottom-start' : 'bottom'}
            style={{ zIndex: 2001 }}
          >
            <ClickAwayListener onClickAway={handleCloseMenu}>
              <DropdownPaper>
                <NavigationButtons
                  onClick={() => {
                    handleButtonClick(RoutesEnum.ContractingList);
                    handleCloseMenu();
                  }}
                  $active={verifyPath(RoutesEnum.ContractingList)}
                  variant="text"
                >
                  <SubtitleMedium>Lojas</SubtitleMedium>
                </NavigationButtons>
                {paymentFeatureEnabled && isStore ? (
                  <NavigationButtons
                    onClick={() => {
                      handleButtonClick(RoutesEnum.PaymentsList);
                      handleCloseMenu();
                    }}
                    $active={verifyPath(RoutesEnum.PaymentsList)}
                    variant="text"
                  >
                    <SubtitleMedium>Pagamentos</SubtitleMedium>
                  </NavigationButtons>
                ) : null}
                {canViewOnlineContracts ? (
                  <NavigationButtons
                    onClick={() => {
                      handleButtonClick(RoutesEnum.ContractingOnline);
                      handleCloseMenu();
                    }}
                    $active={verifyPath(RoutesEnum.ContractingOnline)}
                    variant="text"
                  >
                    <SubtitleMedium>Online</SubtitleMedium>
                  </NavigationButtons>
                ) : null}
              </DropdownPaper>
            </ClickAwayListener>
          </Popper>

          {((isGroupAdmin && sellerFeatureEnabled) || (!isAdmin && !groupAdminCatalogFeatureEnabled) || isMaster) && (
            <>
              <div ref={storesRef} style={{ position: 'relative' }}>
                <NavigationButtons onClick={() => handleToggle('stores')} $active={isStoresActive}>
                  <img src={stores} alt="Ícone de lojas" />
                  {isDesktop && (
                    <>
                      <SubtitleMediumBold style={{ flex: 1 }}>Lojas</SubtitleMediumBold>
                      {openMenu === 'stores' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </>
                  )}
                </NavigationButtons>
              </div>

              <Popper
                open={openMenu === 'stores'}
                anchorEl={storesRef.current}
                placement={!isMobile ? 'bottom-start' : 'bottom'}
                style={{ zIndex: 2001 }}
              >
                <ClickAwayListener onClickAway={handleCloseMenu}>
                  <DropdownPaper>
                    {storeOptions.map(({ label, route }, index) => (
                      <NavigationButtons
                        key={index}
                        onClick={() => {
                          handleButtonClick(route);
                          handleCloseMenu();
                        }}
                        $active={verifyPath(route)}
                        variant="text"
                      >
                        <SubtitleMedium>{label}</SubtitleMedium>
                      </NavigationButtons>
                    ))}
                  </DropdownPaper>
                </ClickAwayListener>
              </Popper>
            </>
          )}
        </NavigationGroup>

        <MenuSelect StartIcon={StorefrontOutlinedIcon} userName={storeLogged.name} />
      </DrawerContainer>
    </DrawerStyled>
  );
};
